import { useCallback, useEffect, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { getLocalStorage } from "@jti/rest-api";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { pageRun } from "@jti/store/slices/loyaltyCoreSlice";
import { type RootStoreState } from "@jti/store/types";

import { isRestrictedSlug } from "../../utils/isRestrictedSlug";

interface PageRunnerProps {
  name: string;
  isInEditor: boolean;
}

export const SCROLL_Y_POS_STORAGE_KEY = "scrollypos";

export const PageRunner: React.FC<PageRunnerProps> = ({ name, isInEditor }) => {
  const dispatch = useDispatch();
  const [shouldRun, setShouldRun] = useState(false);
  const config = { attributeFilter: ["class"], attributeOldValue: true };
  const isLoaded = useSelector((state: RootStoreState) => state.loyaltyCore.isLoaded);
  const { loyaltyRestrictionWithoutRedirectionSlugUrls, isLoyaltyActive, isReferralOn } = useConfig();

  const isRestricted = isRestrictedSlug(loyaltyRestrictionWithoutRedirectionSlugUrls);

  const handleScroll = useCallback(() => {
    const currentPlace = {
      scrollY: window.scrollY,
      url: window.location.href,
    };

    getLocalStorage().set<{ url: string; scrollY: number }>(SCROLL_Y_POS_STORAGE_KEY, currentPlace);
  }, []);

  useEffect(() => {
    const hiddenPageRedirectionLayerSelector = document.querySelector(".protectedBodyPage");
    const observer = new MutationObserver((_, observer) => {
      setShouldRun(true);
      observer.disconnect();
    });

    if (hiddenPageRedirectionLayerSelector && !isInEditor) {
      observer.observe(hiddenPageRedirectionLayerSelector, config);
    } else {
      setShouldRun(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldRun) {
      dispatch(
        pageRun({
          name,
          isRestricted,
          isInEditor,
          isLoyaltyActive: !!isLoyaltyActive,
          isReferralActive: !!isReferralOn,
        })
      );
      if ("scrollRestoration" in history) {
        history.scrollRestoration = "manual";
      }
      window.addEventListener("beforeunload", handleScroll);
      return () => window.removeEventListener("beforeunload", handleScroll);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRun]);

  useEffect(() => {
    if (isLoaded) {
      const scrollYPos = getLocalStorage().get<{ url: string; scrollY: number }>(SCROLL_Y_POS_STORAGE_KEY, {
        url: "",
        scrollY: 0,
      });

      const anchor = window.location.href.split("#")[1];

      if (anchor) {
        document.querySelector(`#${anchor}`)?.scrollIntoView();
      } else if (window.location.href === scrollYPos.url) {
        window.scrollTo({ top: scrollYPos.scrollY, behavior: "smooth" });
      }
    }
  }, [isLoaded]);

  return null;
};
